import { gql, useMutation } from '@apollo/client';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconSync from '@apollo/icons/default/IconSync.svg';
import { Button, Heading, IconButton, Spinner } from '@apollo/orbit';
import { NotFound } from '@apollo/space-kit/illustrations/NotFound';
import classnames from 'classnames';
import _, { identity } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  // CustomCheckViolationListFragment, // only used when rendered in a custom check context
  // OverrideViolationButtonIgnoredRuleFragment, // ignored violations are ignored in this context
  RuleViolationList,
  RuleViolationListLintDiagnosticFragment,
  RuleViolationListLinterRuleLevelConfigurationFragment,
  Violation,
} from 'src/app/graph/checksPage/checkWorkflowTaskDetailsPage/lintCheckDetails/lintCheckList/ruleViolationList/RuleViolationList';
import { getAllSchemaCoordinates } from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import { Icon as BroomIllustration } from 'src/assets/broom.svg';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { tolerantParseSchema } from 'src/lib/chevrotain/tolerantParseSchema';
import { expandedPrint } from 'src/lib/expandedPrint';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { LintDiagnosticLevel } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { useDebouncedDocumentNode } from '../../hooks/useDebouncedDocumentNode';
import { ProposalSchemaInfo } from '../../hooks/useProposalEditorState/useProposalEditorState';
import { useSchemaFromHash } from '../../hooks/useSchemaFromHash';

export const SchemaLinterDrawerLinterRuleLevelConfigurationFragment = gql`
  fragment SchemaLinterDrawerLinterRuleLevelConfigurationFragment on LinterRuleLevelConfiguration {
    rule
    ...RuleViolationListLinterRuleLevelConfigurationFragment
  }
  ${RuleViolationListLinterRuleLevelConfigurationFragment}
`;

export const SchemaLinterDrawer = ({
  onClose,
  className,
  selectedSchemaInfo,
  editor,
  changedSubgraphs,
  graphId,
  rules,
  proposedSubgraphs,
  proposedSupergraphSchema,
}: {
  onClose: () => void;
  className?: string;
  selectedSchemaInfo: ProposalSchemaInfo;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  changedSubgraphs: Record<string, { changed: boolean }>;
  graphId: string;
  rules?: GraphQLTypes.SchemaLinterDrawerLinterRuleLevelConfigurationFragment[];
  proposedSubgraphs: Array<{ name: string; hash: string | undefined }> | null;
  proposedSupergraphSchema: {
    hash: string | null;
    getSdl?: () => string | undefined;
  };
}) => {
  const [lineNumberToScrollTo, setLineNumberToScrollTo] = useState<number>();
  const lastRunSchema = useRef<string>();

  const { data: firstLaunch } = useSchemaFromHash({
    graphId,
    schemaHash: selectedSchemaInfo.firstLaunchHash,
  });

  const initialSchema = firstLaunch?.service?.doc?.source;

  const selectedSubgraphHasChanges = useMemo(
    () =>
      !!changedSubgraphs[selectedSchemaInfo.id]?.changed ||
      initialSchema !== selectedSchemaInfo.text,
    [
      changedSubgraphs,
      initialSchema,
      selectedSchemaInfo.id,
      selectedSchemaInfo.text,
    ],
  );

  const [lintSchema, { data, loading, error, reset }] = useMutation<
    GraphQLTypes.SchemaLinterDrawerLintSchemaMutation,
    GraphQLTypes.SchemaLinterDrawerLintSchemaMutationVariables
  >(gql`
    mutation SchemaLinterDrawerLintSchemaMutation(
      $graphId: ID!
      $baseSdl: String
      $proposedSdl: String!
    ) {
      graph(id: $graphId) {
        lintSchema(baseSdl: $baseSdl, sdl: $proposedSdl) {
          diagnostics {
            ...RuleViolationListLintDiagnosticFragment
          }
          stats {
            ignoredCount
          }
        }
      }
    }
    ${RuleViolationListLintDiagnosticFragment}
  `);

  const editorTextValue = useDerivedEditorValue({
    editor,
    getValue: identity,
    withPosition: false,
  });

  // Note: When users switch between schemas we should
  // not continue showing the current diagnostics
  useEffect(() => {
    reset();
    setLineNumberToScrollTo(undefined);
  }, [selectedSchemaInfo.id, reset]);

  const hasRunLinter = data || loading || error;
  const shouldShowRerunButton = data && !loading && !error;

  const hasChangedSinceLastRun = useDerivedEditorValue({
    editor,
    getValue: (text) => text !== lastRunSchema.current,
    withPosition: false,
  });

  const handleClose = useCallback(() => {
    onClose();
    reset();
    setLineNumberToScrollTo(undefined);
  }, [onClose, reset]);

  const debouncedDocumentNode = useDebouncedDocumentNode(editor);

  const allSchemaCoordinates = useMemo(() => {
    // NOTE: In order to get all the coordinates we need to print each on its
    // own line otherwise we end up with duplicates in our list.
    const expandedDocumentNode = tolerantParseSchema(
      monacoEditor.editor.createModel(expandedPrint(debouncedDocumentNode)),
    ).documentNode;

    return getAllSchemaCoordinates(expandedDocumentNode);
  }, [debouncedDocumentNode]);

  const handleRunLinter = useCallback(() => {
    setLineNumberToScrollTo(undefined);
    const currentValue = editor.getValue();
    lastRunSchema.current = currentValue;
    try {
      lintSchema({
        variables: {
          graphId,
          baseSdl: initialSchema,
          proposedSdl: currentValue,
        },
      });
    } catch (e) {
      // Error is handled outside of this so we ignore here
    }
  }, [editor, graphId, initialSchema, lintSchema]);

  const onClickViolationLine = useCallback((violation: Violation) => {
    setLineNumberToScrollTo(violation.sourceLocations?.[0]?.start?.line);
  }, []);

  const lintResult = data?.graph?.lintSchema;

  const allDeltaDecorations = React.useMemo(() => {
    const monacoDeltaDecorations: monacoEditor.editor.IModelDeltaDecoration[] =
      [];
    if (lineNumberToScrollTo) {
      monacoDeltaDecorations.push({
        range: new monacoEditor.Range(
          lineNumberToScrollTo,
          1,
          lineNumberToScrollTo,
          1,
        ),
        options: {
          isWholeLine: true,
          className: 'border-y border-dashed border-highlight !bg-highlight',
          linesDecorationsClassName:
            'border-y border-dashed border-highlight !bg-highlight',
          marginClassName:
            'border-y border-dashed border-highlight !bg-highlight',
        },
      });
    }

    return {
      modelDecorations: [...(monacoDeltaDecorations ?? [])],
    };
  }, [lineNumberToScrollTo]);

  const model = useEditorModel(editor);
  React.useEffect(() => {
    if (!model) return;

    const decorations = editor.createDecorationsCollection(
      allDeltaDecorations.modelDecorations,
    );

    // Clean up decorations
    return () => {
      decorations.clear();
    };
    // this needs to happen on mount and when text is loaded,
    // so update whenever the editor text changes
  }, [
    editor,
    allDeltaDecorations,
    lineNumberToScrollTo,
    model,
    editorTextValue,
  ]);

  React.useEffect(() => {
    if (lineNumberToScrollTo) {
      editor.revealLineInCenterIfOutsideViewport(lineNumberToScrollTo);
    }
  }, [editor, lineNumberToScrollTo]);

  const [groupedErrorResults, groupedWarningResults] = useMemo(
    () =>
      _.partition(
        lintResult?.diagnostics
          .filter(
            (summary): summary is NonNullable<typeof summary> => !!summary,
          )
          .sort((a, b) => a.rule.localeCompare(b.rule)),
        (violation) => violation.level === LintDiagnosticLevel.ERROR,
      ).map((violations) =>
        _.groupBy(violations, (violation) => violation.rule),
      ),
    [lintResult?.diagnostics],
  );

  return (
    <div
      className={classnames(
        'z-30 flex h-full w-[26rem] flex-col border-l border-l-primary bg-primary 4xl:w-[37rem]',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-b-primary px-1 py-2">
        <Heading as="h3" size="lg" className="pl-3">
          Schema Linting
        </Heading>
        <div className="flex items-center space-x-2">
          {shouldShowRerunButton && (
            <Tooltip
              label={
                hasChangedSinceLastRun
                  ? 'Rerun linter'
                  : 'No changes since last run'
              }
              shouldWrapChildren={!hasChangedSinceLastRun}
              placement="top"
            >
              <IconButton
                variant="hidden"
                aria-label="Rerun linter"
                isDisabled={!hasChangedSinceLastRun}
                icon={<IconSync />}
                onClick={handleRunLinter}
              />
            </Tooltip>
          )}
          <IconButton
            variant="hidden"
            aria-label="Close drawer"
            icon={<IconClose />}
            onClick={handleClose}
          />
        </div>
      </div>
      <div
        className={classnames(
          'flex min-h-0 flex-1 flex-col overflow-y-auto',
          !isMinLengthArray(1, lintResult?.diagnostics) || loading || error
            ? 'items-center justify-center'
            : 'px-6',
        )}
      >
        {!hasRunLinter ? (
          <>
            <BroomIllustration className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              Lint your schema
            </h3>
            <p className="mb-3 w-[40ch] text-center text-base text-secondary">
              Stay ahead by catching and correcting errors as early as possible.
            </p>
            <Tooltip
              label="Linter can only run on schemas that have been edited. Please make changes to continue."
              disabled={selectedSubgraphHasChanges}
              shouldWrapChildren={!selectedSubgraphHasChanges}
            >
              <Button
                isDisabled={!selectedSubgraphHasChanges}
                onClick={handleRunLinter}
              >
                Run the Linter
              </Button>
            </Tooltip>
          </>
        ) : loading ? (
          <>
            <Spinner size="md" className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              Linting in progress
            </h3>
            <p className="mb-3 w-[40ch] text-center text-base text-secondary">
              Checking against your team's rules to make sure everything is in
              order...
            </p>
          </>
        ) : error ? (
          <>
            <NotFound className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              Something went wrong
            </h3>
            <p className="mb-3 w-[40ch] text-center text-base text-secondary">
              Try re-running the linter.
            </p>
            <Button onClick={handleRunLinter}>Run linter</Button>
          </>
        ) : !isMinLengthArray(1, lintResult?.diagnostics) ? (
          <>
            <BroomIllustration className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              No linting issues detected
            </h3>
            <p className="mb-3 w-[40ch] text-center text-base text-secondary">
              Hooray! Tidiest schema in town (or the galaxy!)
            </p>
          </>
        ) : (
          <div className="my-6 space-y-3">
            {Object.entries(groupedErrorResults ?? {}).map(
              ([ruleName, violations]) => {
                return (
                  <RuleViolationList
                    key={ruleName}
                    violations={violations}
                    name={ruleName}
                    isSupergraph={false}
                    subgraphName={selectedSchemaInfo.id}
                    canIgnoreLines={false}
                    rule={rules?.find((rule) => rule.rule === ruleName)}
                    activeSchemaCoordinates={allSchemaCoordinates}
                    onClickLine={onClickViolationLine}
                    proposedSubgraphs={proposedSubgraphs}
                    proposedSupergraphSchema={proposedSupergraphSchema}
                    graphId={graphId}
                    // TODO: should we show messages for individual violations?
                    showMessage={false}
                    ignoredLinterViolations={undefined}
                  />
                );
              },
            )}
            {Object.entries(groupedWarningResults ?? {}).map(
              ([ruleName, violations]) => {
                return (
                  <RuleViolationList
                    key={ruleName}
                    violations={violations}
                    name={ruleName}
                    isSupergraph={false}
                    subgraphName={selectedSchemaInfo.id}
                    canIgnoreLines={false}
                    rule={rules?.find((rule) => rule.rule === ruleName)}
                    activeSchemaCoordinates={allSchemaCoordinates}
                    onClickLine={onClickViolationLine}
                    proposedSubgraphs={proposedSubgraphs}
                    proposedSupergraphSchema={proposedSupergraphSchema}
                    graphId={graphId}
                    // TODO: should we show messages for individual violations?
                    showMessage={false}
                    ignoredLinterViolations={undefined}
                  />
                );
              },
            )}
          </div>
        )}
      </div>
    </div>
  );
};
